"use client";

import React, { useState, useMemo, useRef, useEffect } from "react";
import { useIsClient } from "@thenounproject/lingo-core";
import useCurrentUser from "@actions/users/useCurrentUser";
import MobileNavComponent from "./MobileNav";
import DesktopNavComponent from "./DesktopNav";

export type LinkItem = {
  id: string;
  text: string;
  subtitle?: string;
  link: string;
  external?: boolean;
};

export type HighlightedItem = {
  title: string;
  subtitle?: string;
  icon?: string;
  image?: { src: string; alt: string };
  button: {
    text: string;
    link: string;
    external?: boolean;
  };
};

export type ColumnData = {
  title?: string;
  items: LinkItem[];
};

export type MenuItem = {
  id: string;
  text: string;
  link?: string;
  dropdown?: {
    highlightedItem?: HighlightedItem;
    columns: ColumnData[];
  };
};

export type MobileMenuItem = {
  id: string;
  text: string;
  link?: string;
  columns: ColumnData[];
  dropdown: { highlightedItem?: HighlightedItem };
};

// Navigation Links
export const NAV_LINKS = {
  // Product Features
  portals: {
    id: "portals",
    text: "Brand Portals",
    link: "/product/portals",
    subtitle: "A centralized hub to share brand assets and kits.",
  },
  kits: {
    id: "kits",
    text: "Kits",
    link: "/product/kits",
    subtitle: "Organize and showcase assets with built-in guidelines.",
  },
  library: {
    id: "library",
    text: "Library",
    link: "/product/library",
    subtitle: "Your single source of truth for every asset.",
  },
  userManagement: {
    id: "user-management",
    text: "User Management",
    link: "/product/user-management",
  },
  fileConversion: {
    id: "file-conversion",
    text: "File Conversion & Downloads",
    link: "/product/convert-and-resize",
  },
  directLinks: {
    id: "direct-links",
    text: "Direct Links",
    link: "/product/direct-links",
  },
  insights: {
    id: "insights",
    text: "Insights",
    link: "/product/insights",
  },
  metadata: {
    id: "metadata",
    text: "Metadata & Tagging",
    link: "/product/metadata-and-tagging",
  },
  visualWorkspace: {
    id: "visual-workspace",
    text: "Visual Workspace",
    link: "/product/visual-workspace",
  },
  integrations: {
    id: "integrations",
    text: "Integrations",
    link: "/product/integrations",
  },

  // Solutions
  brandGuidelines: {
    id: "brand",
    text: "Digital Brand Guidelines",
    link: "/solutions/brand-guidelines",
    subtitle: "Keep brand assets and usage rules together.",
  },
  dam: {
    id: "dam",
    text: "Digital Asset Management",
    link: "/solutions/digital-asset-management",
    subtitle: "Store, organize, and manage assets effortlessly.",
  },
  brandPortals: {
    id: "brand-portals",
    text: "Digital Brand Hub",
    link: "/solutions/digital-brand-hub",
    subtitle: "A central source for all brand assets.",
  },
  salesEnablement: {
    id: "sales-enablement",
    text: "Sales Enablement",
    link: "/solutions/sales-enablement",
    subtitle: "Give teams instant access to sales content.",
  },

  // Industries
  technology: {
    id: "technology",
    text: "Technology",
    link: "/industry/technology",
  },
  higherEducation: {
    id: "higher-education",
    text: "Higher Education",
    link: "/industry/higher-education",
  },
  retail: {
    id: "retail",
    text: "Retail",
    link: "/industry/retail",
  },
  finance: {
    id: "finance",
    text: "Finance",
    link: "/industry/finance",
  },
  athletics: {
    id: "athletics",
    text: "Athletics",
    link: "/industry/athletics",
  },
  cannabis: {
    id: "cannabis",
    text: "Cannabis",
    link: "/industry/cannabis",
  },
  localGovernment: {
    id: "local-government",
    text: "Local Government",
    link: "/industry/local-government",
  },

  // Teams
  brandTeams: {
    id: "brand-teams",
    text: "Brand Teams",
    link: "/team/brand-teams",
  },
  agencies: {
    id: "agencies",
    text: "Agencies",
    link: "/team/agency",
  },
  enterprise: {
    id: "enterprise",
    text: "Enterprise",
    link: "/team/enterprise",
  },
  smallMediumBusinesses: {
    id: "small-medium-businesses",
    text: "Small & Medium Businesses",
    link: "/team/small-medium",
  },

  // Switch From
  googleDrive: {
    id: "google-drive",
    text: "Google Drive",
    link: "/solutions/google-drive-alternative",
    subtitle: "Outgrown Drive's folder-based chaos?",
  },
  dropbox: {
    id: "dropbox",
    text: "Dropbox",
    link: "/solutions/dropbox-alternative",
    subtitle: "Move beyond static asset storage.",
  },

  // Resources
  customerStories: {
    id: "customer-stories",
    text: "Customer Stories",
    link: "/blog/collection/customer-stories",
    subtitle: "See how customers use Lingo",
  },
  pricing: {
    id: "pricing",
    text: "Pricing",
    link: "/pricing",
    subtitle: "Choose the right plan for you",
  },
  about: {
    id: "about",
    text: "About Us",
    link: "/about",
    subtitle: "We're Lingo, and we're thrilled to see you here!",
  },
  blog: {
    id: "blog",
    text: "Blog",
    link: "/blog",
    subtitle: "Insights and strategies for better brand management.",
  },
  help: {
    id: "help",
    text: "Help",
    link: "https://help.lingoapp.com",
    external: true,
    subtitle: "Get answers and support for Lingo.",
  },
  contact: {
    id: "contact",
    text: "Contact Us",
    link: "/contact",
    subtitle: "Reach out to our team anytime!",
  },
  whatIsDam: {
    id: "dam",
    text: "What is DAM?",
    link: "/blog/what-is-digital-asset-management-dam",
    subtitle: "Learn about digital asset management",
  },
  glossary: {
    id: "glossary",
    text: "Glossary",
    link: "/glossary",
    subtitle: "Digital asset management terms defined",
  },
};

// Highlighted items for dropdowns
const HIGHLIGHTS = {
  product: {
    title: "Digital Asset Management",
    subtitle: "A smarter, simpler way to manage assets.",
    icon: "marketing.style-guide",
    button: {
      text: "Explore Lingo",
      link: NAV_LINKS.dam.link,
    },
  },
  resources: {
    title: "What is DAM?",
    subtitle: "Store, organize, and distribute digital assets easily.",
    icon: "marketing.storage",
    button: {
      text: "Learn More",
      link: NAV_LINKS.whatIsDam.link,
    },
  },
};

// Main menu structure
const menuItems: MenuItem[] = [
  {
    id: "product",
    text: "Product",
    dropdown: {
      highlightedItem: HIGHLIGHTS.product,
      columns: [
        {
          title: "By Use Case",
          items: [
            NAV_LINKS.brandGuidelines,
            NAV_LINKS.dam,
            NAV_LINKS.brandPortals,
            NAV_LINKS.salesEnablement,
          ],
        },
        {
          title: "Brand Foundations",
          items: [NAV_LINKS.portals, NAV_LINKS.kits, NAV_LINKS.library],
        },
        {
          title: "Core Capabilities",
          items: [
            NAV_LINKS.userManagement,
            NAV_LINKS.fileConversion,
            NAV_LINKS.directLinks,
            NAV_LINKS.insights,
            NAV_LINKS.metadata,
            NAV_LINKS.visualWorkspace,
            NAV_LINKS.integrations,
          ],
        },
      ],
    },
  },
  {
    id: "solutions",
    text: "Who We Help",
    dropdown: {
      highlightedItem: HIGHLIGHTS.product,
      columns: [
        {
          title: "By Industry",
          items: [
            NAV_LINKS.technology,
            NAV_LINKS.retail,
            NAV_LINKS.finance,
            NAV_LINKS.localGovernment,
            NAV_LINKS.higherEducation,
            NAV_LINKS.athletics,
            NAV_LINKS.cannabis,
          ],
        },
        {
          title: "By Team",
          items: [
            NAV_LINKS.brandTeams,
            NAV_LINKS.agencies,
            NAV_LINKS.smallMediumBusinesses,
            NAV_LINKS.enterprise,
          ],
        },
        {
          title: "Switch From",
          items: [NAV_LINKS.googleDrive, NAV_LINKS.dropbox],
        },
      ],
    },
  },
  NAV_LINKS.customerStories,
  NAV_LINKS.pricing,
  {
    id: "resources",
    text: "Resources",
    dropdown: {
      highlightedItem: HIGHLIGHTS.resources,
      columns: [
        {
          items: [NAV_LINKS.blog, NAV_LINKS.glossary],
        },
        {
          items: [NAV_LINKS.help, NAV_LINKS.contact],
        },
        {
          items: [NAV_LINKS.about],
        },
      ],
    },
  },
];

// Custom hook for scroll locking
const useScrollLock = (isLocked: boolean) => {
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient || !isLocked) return;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const originalStyle = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
    };

    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = originalStyle.overflow;
      document.body.style.paddingRight = originalStyle.paddingRight;
    };
  }, [isLocked, isClient]);
};

export default function Header() {
  useCurrentUser();
  const [showMenu, setShowMenu] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileItemOpen, setMobileItemOpen] = useState<string | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);

  useScrollLock(activeDropdown !== null || showMenu);

  useEffect(() => {
    if (!activeDropdown && !showMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isMenuButton = (target as Element).closest(".menu-button");
      const isLink = (target as Element).closest("a");

      if (navRef.current && !navRef.current.contains(target)) {
        setActiveDropdown(null);
      }
      if (isLink) {
        setActiveDropdown(null);
        setShowMenu(false);
        setMobileItemOpen(null);
        return;
      }

      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target) && !isMenuButton) {
        setShowMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [activeDropdown, showMenu]);

  const mobileMenuItems = useMemo<MobileMenuItem[]>(
    () =>
      menuItems.map(item => {
        if (!item.dropdown) {
          return {
            id: item.id,
            text: item.text,
            link: item.link,
            columns: [],
            dropdown: null,
          };
        }

        return {
          id: item.id,
          text: item.text,
          columns: item.dropdown.columns,
          dropdown: {
            highlightedItem: item.dropdown.highlightedItem,
          },
        };
      }),
    []
  );

  return (
    <>
      <DesktopNavComponent
        showMenu={showMenu}
        navRef={navRef}
        menuItems={menuItems}
        activeDropdown={activeDropdown}
        setActiveDropdown={setActiveDropdown}
        setShowMenu={setShowMenu}
      />
      <MobileNavComponent
        showMenu={showMenu}
        mobileMenuItems={mobileMenuItems}
        mobileMenuRef={mobileMenuRef}
        mobileItemOpen={mobileItemOpen}
        setMobileItemOpen={setMobileItemOpen}
      />
    </>
  );
}
